import DynamicSectionElement from '@/js/dynamic-section-element';
import ProductReviews from '../product-reviews/product-reviews';
import { addItemsToCart } from '@/js/cart-utils';
import { initBackInStock, BISEventListeners } from '@/js/back-in-stock-helper';

class ProductMain extends DynamicSectionElement {
  connectedCallback() {
    this.initVariables();
    this.productFormHandlers();
    this.fabricLinkHandler();
    this.showSizesByLengthFit();
    BISEventListeners();
    this.openBISModal();

    this.firstLoad = true;
    this.loadKiwiSizingChart();
  }

  /**
   * Initialize variables for the product form and variants.
   */
  initVariables() {
    this.formElement = this.querySelector('form');
    this.variantId = this.formElement.querySelector('[name="id"]').value;
    this.wishlistButton = document.querySelector('wishlist-button-product');
    this.kiwiSizingAttributes = this.querySelector(
      '[data-kiwi-sizing-attributes]'
    );
    this.productReviewsWidget = document.querySelector(
      '[data-product-reviews]'
    );
    this.productStarRatingWidget = document.querySelector(
      '[data-judgeme-stars]'
    );

    if (this.querySelector('[data-variants]')) {
      this.variants = JSON.parse(
        this.querySelector('[data-variants]').dataset.variants
      );
    }

    if (!this.variants?.length) return;

    //Use the pre-selected variant ID
    if (this.variantId) {
      this.variant = this.findVariantById(this.variantId);
    } else if (!this.variant && this.variants.length === 1) {
      // If no variant is selected and we only have one variant, set as selected
      this.variant = this.variants[0];
      this.variantId = this.variant.id;
    }

    if (this.querySelector('[data-product-json]')) {
      this.product = JSON.parse(
        this.querySelector('[data-product-json]').dataset.productJson
      );
    }

    // Initialize the BIS modal only if we have variants and a selected variant
    if (this.variants?.length > 0 && this.variant) {
      initBackInStock(this.product);
    }

    this.soldOutCTA = this.querySelector('[data-add-to-cart="sold-out"]');
  }

  /**
   * Bind event listeners for the product form and handles adding a product to the cart and to the bundle.
   */
  productFormHandlers() {
    this.formElement.addEventListener('change', event => {
      if (event.target.closest('[data-option]')) {
        this.onOptionChange(event);
      }
    });

    this.formElement.addEventListener('submit', event => {
      event.preventDefault();
      const form = event.target;
      const formData = new FormData(form);
      const id = this.variantId;
      const quantity = Number(formData.get('quantity'));
      const items = [{ id, quantity }];
      const ctaButton = event.target.querySelector('[data-add-to-cart]');

      if (ctaButton.dataset.addToCart === 'sold-out') {
        // Open up the BIS modal
        document.dispatchEvent(
          new CustomEvent('bis:popupModal', {
            detail: { variantId: this.variantId, product: this.product }
          })
        );
      } else {
        addItemsToCart(items, ctaButton);
      }
    });

    this.formElement.addEventListener('click', event => {
      if ('soldOutCta' in event.target.dataset) {
        this.smallestSoldOutVariant = this.findSmallestSoldOutVariant();

        if (!this.smallestSoldOutVariant) {
          return;
        }
        document.dispatchEvent(
          new CustomEvent('bis:popupModal', {
            detail: {
              variantId: this.smallestSoldOutVariant.id,
              product: this.product
            }
          })
        );
      } else if ('colorSwatchItem' in event.target.dataset) {
        this.onSwatchClick(event.target);
      }
    });
  }

  /**
   * Do a section re-render for the selected color swatch (aka product).
   * @param {Element} swatchElement
   */
  async onSwatchClick(swatchElement) {
    const colorProductPath = swatchElement.dataset.swatchProductUrl;
    const { origin } = window.location;

    await this.loadSectionFromUrl(`${origin}${colorProductPath}`, {
      replaceState: true,
      useMorphdom: true
    });

    this.reloadCevoid();

    // Re render the product recommendations section on a swatch change
    const recommendationsElement = document.querySelector(
      'product-recommendations'
    );

    if (recommendationsElement) {
      const productId = this.product.id;
      // Take the data-url and substitute the product_id
      const newUrl = recommendationsElement.dataset.url.replace(
        /product_id=\d+/,
        `product_id=${productId}`
      );
      recommendationsElement.dataset.url = newUrl;
      await recommendationsElement.loadRecommendations();
    }
  }

  /**
   * When fabric links are clicked, uses section rendering to update values to that product
   * @returns {void}
   */
  fabricLinkHandler() {
    const fabricLinks = this.querySelector('[data-fabric-links]');
    if (!fabricLinks) return;
    fabricLinks.addEventListener('click', event => {
      event.preventDefault();
      if ('fabricLink' in event.target.dataset) {
        this.onFabricClick(event.target);
      }
    });
  }

  /**
   * Do a section re-render for the selected fabric (aka product).
   * @param {HTMLElement} fabricElement
   */
  async onFabricClick(fabricElement) {
    const url = fabricElement.href;
    await this.loadSectionFromUrl(url, {
      replaceState: true,
      useMorphdom: true
    });

    if (this.productReviewsWidget || this.productStarRatingWidget) {
      const productId = fabricElement.dataset.productId;
      ProductReviews.loadReviewsForProduct(productId);
    }

    // After the product main section is re-rendered (top priority) we do a subsequent re-render of other sections if required.
    this.rerenderAdditionalSections(url);
  }

  /**
   * Handle updating the product form when a variant is selected.
   * @param {Event} event
   * @returns {Promise<void>}
   */
  async onOptionChange(event) {
    const selectedColor = this.formElement.querySelector(
      '[data-option="color"] input[type="radio"]:checked'
    );
    const selectedLengthFit = this.formElement.querySelector(
      '[data-option="lengthFit"] input[type="radio"]:checked'
    );
    const selectedSize = this.formElement.querySelector(
      '[data-option="size"] input[type="radio"]:checked'
    );

    const matchingVariant = this.variants.find(variant => {
      const variantColor = variant.color;
      const variantLengthFit = variant.lengthFit;
      const variantSize = variant.size;

      const colorIsMatch = variantColor
        ? variantColor == selectedColor.value
        : true;
      const lengthFitIsMatch = variantLengthFit
        ? variantLengthFit == selectedLengthFit.value
        : true;
      const sizeIsMatch = variantSize
        ? variantSize == selectedSize?.value
        : true;

      return colorIsMatch && lengthFitIsMatch && sizeIsMatch;
    });

    const { origin, pathname } = window.location;
    let parameters;
    let url = `${origin}${pathname}`;

    if (matchingVariant) {
      parameters = new URLSearchParams({
        variant: matchingVariant.id
      });

      this.variantId = matchingVariant.id;
      url = url + `?${parameters}`;
    } else {
      this.variantId = undefined;
    }

    await this.loadSectionFromUrl(url, {
      replaceState: true,
      useMorphdom: true
    });

    if (!matchingVariant) {
      // If the length fit value is clicked, if there isn't a variant match, then we manually set the value.
      const lengthFitOptionClicked = event.target.closest(
        '[data-option="lengthFit"]'
      );

      if (lengthFitOptionClicked) {
        this.setLengthFitValue(event.target.value);
      }
    }
  }

  /**
   * Re-render additional sections after the product main section is re-rendered.
   * @param {string} url - the URL to re-render the sections for
   */
  rerenderAdditionalSections(url) {
    const sectionsToRerender = document.querySelectorAll(
      '[data-updates-with-fabric]'
    );
    if (sectionsToRerender.length > 0) {
      const sectionIdsToRerender = [...sectionsToRerender].map(
        section => section.dataset.sectionId
      );

      DynamicSectionElement.reRenderMultipleSections(sectionIdsToRerender, {
        url,
        useMorphdom: true
      });
    }
  }

  resetProductImageCarousel() {
    // Reset the image carousel as image count may have changed, and need to ensure first image is shown.
    const carousel = this.querySelector('[data-product-main-images]');
    if (carousel?.glideInstance) {
      carousel.resetCarousel();
    }
  }

  /**
   * Handle the re-render of the section after a variant is selected.
   */
  afterSectionRender() {
    this.initVariables();
    this.showSizesByLengthFit();
    this.openBISModal();
    this.updateWishlistButton();
    this.resetProductImageCarousel();
    this.loadKiwiSizingChart();
    this.useFullResProductImages();
  }

  /**
   * Fetch the kiwisizing attributes from the HTML div tag & reload the KiwiSizing modal
   */
  loadKiwiSizingChart() {
    window.KiwiSizing =
      window.KiwiSizing === undefined ? {} : window.KiwiSizing;

    const productId = this.kiwiSizingAttributes.dataset.productId;

    // If the product isn't changed do not update kiwi Sizing Modal
    // Bypass condition for first page load
    if (!this.firstLoad && window.KiwiSizing.data?.product === productId) {
      return;
    }

    this.firstLoad = false;

    const collections = JSON.parse(
      this.kiwiSizingAttributes.dataset.collections
    );
    const tags = JSON.parse(this.kiwiSizingAttributes.dataset.tags);

    const vendor = JSON.parse(this.kiwiSizingAttributes.dataset.vendor);
    const type = JSON.parse(this.kiwiSizingAttributes.dataset.type);
    const title = JSON.parse(this.kiwiSizingAttributes.dataset.title);
    const images = JSON.parse(this.kiwiSizingAttributes.dataset.images);
    const options = JSON.parse(this.kiwiSizingAttributes.dataset.options);
    const variants = JSON.parse(this.kiwiSizingAttributes.dataset.variantsKs);

    window.KiwiSizing.data = {
      collections: collections,
      tags: tags,
      product: productId,
      vendor: vendor,
      type: type,
      title: title,
      images: images,
      options: options,
      variants: variants
    };

    // Hide the button if there is no sizing guide associated with the product
    window.ks.loadSizing({
      productData: window.KiwiSizing.data,
      loaded: function (success) {
        if (success) {
          document.querySelector('#size-guide').classList.remove('hidden');
        } else {
          document.querySelector('#size-guide').classList.add('hidden');
        }
      }
    });
  }

  /**
   * Update the data attributes for the wishlist button
   */
  updateWishlistButton() {
    if (!this.wishlistButton) return;
    this.wishlistButton.dataset.variantId = this.variantId;
    this.wishlistButton.dataset.productHandle = this.product.handle;
  }

  /**
   * Set the length fit value in the form.
   * @param {String} value
   */
  setLengthFitValue(lengthFit) {
    const lengthFitInput = this.formElement.querySelector(
      `[data-option="lengthFit"] input[value="${lengthFit}"]`
    );

    if (lengthFitInput) {
      lengthFitInput.checked = true;
    }

    this.showSizesByLengthFit();
  }

  /**
   * Show the corresponding sizes for the selected length/fit.
   * @returns {void}
   */
  showSizesByLengthFit() {
    // Get the selected length/fit value - by default we always have a select length/fit value
    const selectedLengthFit = this.formElement.querySelector(
      '[data-option="lengthFit"] input[type="radio"]:checked'
    )?.value;

    const sizeOptions = this.formElement.querySelectorAll('[data-size-option]');

    // Hide all size options
    for (const sizeOption of sizeOptions) {
      sizeOption.classList.add('hidden');
    }

    for (const sizeOption of [...sizeOptions].filter(
      sizeOption => sizeOption.dataset.lengthFitType === selectedLengthFit
    )) {
      sizeOption.classList.remove('hidden');
    }
  }

  /**
   * Find the smallest sold out variant based on the selected size and length - used for the BIS modal
   * @returns {object} - the smallest sold out variant
   */
  findSmallestSoldOutVariant() {
    // Only return variants of a selected length/fit if selected
    const selectedLengthFit = this.formElement.querySelector(
      '[data-option="lengthFit"] input[type="radio"]:checked'
    )?.value;

    if (!selectedLengthFit) {
      return this.variants.find(variant => {
        return variant.available === false;
      });
    }

    return this.variants.find(variant => {
      return (
        variant.lengthFit === selectedLengthFit && variant.available === false
      );
    });
  }

  /**
   * Open the BIS modal if the variant is out of stock and all options are selected.
   * @returns {void}
   */
  openBISModal() {
    // If the CTA is sold out, open the BIS modal
    if (this.soldOutCTA && !this.variant.available) {
      document.dispatchEvent(
        new CustomEvent('bis:popupModal', {
          detail: { variantId: this.variantId, product: this.product }
        })
      );
    }
  }

  /**
   * Find the matching variant by id
   * @param {number} id - the variant id
   * @returns {object|undefined} variant or undefined if not found
   */
  findVariantById(id) {
    if (!this.variants?.length || !id) return;

    return this.variants.find(variant => Number(variant.id) === Number(id));
  }

  /**
   * Reinitialize the image zoom elements after a variant is selected to prevent small images from being zoomed in after a variant change.
   */
  useFullResProductImages() {
    const imageZoomElements = this.querySelectorAll('image-zoom');
    for (const imageZoomElement of imageZoomElements) {
      imageZoomElement.setFullResImageSrc();
    }
  }

  /**
   * Reload the Cevoid app
   * @returns {void}
   */
  reloadCevoid() {
    window.cevoid.reloadAll();
  }
}

customElements.get('product-main') ||
  customElements.define('product-main', ProductMain);

export default ProductMain;
